import ApiServer from "../ApiServer.ts";
import * as amplify from "./amplify.ts";
import * as Html from "./Html.ts";

// Type definitions for progressive jQuery removal
type ValidationFunction = (input: HTMLElement) => boolean;


var
  // Constants

  // CSS class applied to valid form controls
  VALID_CLASS = "fw-form-valid-control",

  // CSS class applied to invalid form controls
  INVALID_CLASS = "fw-form-invalid-control",

  // CSS selector for invalid form controls
  INVALID_SELECTOR = "."+INVALID_CLASS,

  // CSS selector for inputs
  INPUT_SELECTOR = "input,select,textarea",

  // data attribute set to false initially and to true after first submission
  // to prevent the display of errors before the whole form has been filled
  FORM_VALIDATION_ATTRIBUTE = "data-fw-form-validation",

  // value of the form validation attribute to enable validation
  FORM_VALIDATION_ACTIVE = "active";

/**
 * Checks if a form input is empty
 *
 * @param input - The input element to check
 * @returns true if the input is considered empty
 */
export function isDomEmpty(input: HTMLInputElement): boolean {
  if(input.type === "file"){
    return input.files?.length === 0;
  }
  let value = input.value = input.value.trim();
  return (
    value === "" || (input.type === "checkbox" && !input.checked)
  );
}

/**
 * Native implementation: removes alert element following the given element
 * @param element - Native HTMLElement
 */
function removeBeforeAlertNative(element: HTMLElement): void {
  const nextElement = element.nextElementSibling;
  if (nextElement?.classList.contains('fw-input-alert')) {
    nextElement.remove();
  }
}


/**
 * Native implementation: Style an element as valid
 * @param element - Native HTMLElement
 */
function markValidNative(element: HTMLElement): void {
  removeBeforeAlertNative(element);
  element.classList.remove(INVALID_CLASS);
  element.classList.add(VALID_CLASS);
  setTimeout(() => {
    element.classList.remove(VALID_CLASS);
  }, 500);
}


/**
 * Native implementation: Style an element as invalid
 * @param element - Native HTMLElement
 * @param message - Optional error message to display
 */
function markInvalidNative(element: HTMLElement, message?: string): void {
  removeBeforeAlertNative(element);
  element.classList.remove(VALID_CLASS);
  element.classList.add(INVALID_CLASS);

  if (message) {
    const alertNode = document.createElement('div');
    alertNode.className = 'fw-input-alert';
    alertNode.innerHTML = `<span>${message}</span>`;
    element.insertAdjacentElement('afterend', alertNode);

    const inputElem = element as HTMLInputElement;
    if (inputElem.type !== 'checkbox') {
      const elemRect = element.getBoundingClientRect();
      const parentRect = element.offsetParent?.getBoundingClientRect();
      if (parentRect) {
        alertNode.style.bottom = (parentRect.bottom - elemRect.top + 5) + 'px';
        alertNode.style.right = (parentRect.right - elemRect.right) + 'px';
      }
    }

    setTimeout(() => { alertNode.style.transform = 'unset'; }, 0);

    if (element.closest('.V4')) {
      setTimeout(() => {
        alertNode.style.transition = 'opacity 0.5s';
        alertNode.style.opacity = '0';
        setTimeout(() => alertNode.remove(), 500);
      }, 2000);
    }
  }
}
/**
 * Base function for the validation of form controls
 *
 * This function is intended as a helper for the development of more specific
 * methods for different types of form controls: input, select, password,
 * email, date...
 *
 * @param input - HTMLInputElement to validate
 * @param isValid - Function to check validity; returns true when valid, false for invalid, any other value skips validation
 * @param errorMessage - Error message to display when invalid
 */
function checkInput(input: HTMLInputElement, isValid: ValidationFunction, errorMessage: string): void {
  var
    form = input.form,
    errorDisplayTarget :HTMLElement = input;
  if(input.type==='hidden'){
    errorDisplayTarget = input.parentElement;
  }
  function checkHandler(){
    if (form.getAttribute(FORM_VALIDATION_ATTRIBUTE) !== FORM_VALIDATION_ACTIVE) {
      // skip validation until first submission
      return;
    }
    try{
      switch(isValid(input)){
        case true:
          markValidNative(errorDisplayTarget);
          break;
        case false:
          markInvalidNative(errorDisplayTarget,errorMessage);
          break;
        default:
          // any other value skips the validation
      }
    }catch(e : any){
      markInvalidNative(errorDisplayTarget,e.message);
    }
  }
  input.addEventListener("blur", checkHandler);
  input.addEventListener("change", checkHandler);
  input.addEventListener("show", checkHandler);
}

/**
 * Validates all form controls within a form
 *
 * @param form - The form element to validate
 * @returns true if form is valid, false otherwise
 */
function validate(form: HTMLFormElement): boolean {
  form.setAttribute(FORM_VALIDATION_ATTRIBUTE, FORM_VALIDATION_ACTIVE);
  try{
    // blur each control to trigger handler registered for validation
    form.querySelectorAll<HTMLElement>(INPUT_SELECTOR).forEach((input)=>{
      input.classList.remove(INVALID_CLASS);
      input.classList.remove(VALID_CLASS);
      //the blur trigger the validation of each items
      input.dispatchEvent(new Event('blur'))
    })
  }catch(e){
    console.log(e);
  }

  var invalidInput = form.querySelector<HTMLElement>(INVALID_SELECTOR)
  if (invalidInput!==null ){
    // focus first invalid input
    invalidInput.focus();
    return false;
  }
  return true;
}
/**
 * Form submission handler
 * Validates form before submit, handles API calls if configured
 * Supports amplify event publishing on success
 */
/*
  validate form before submit, if specific api form (always the case in ou site) we call it
  if amplify attribute use it as success idem for error
*/
document.addEventListener('submit', async function(event) {

  const form = event.target as HTMLFormElement;
  if (!form || form.tagName !== 'FORM') {
    console.log('[FORMS] Target is not a form, ignoring');
    return;
  }

  var api, apiName, onSuccess;
  try {
    // lock resubmit
    if (form.getAttribute('data-submit-running')) {
      console.log('[FORMS] Form already running, preventing submit');
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }
    form.setAttribute('data-submit-running', 'true');

    if (form.getAttribute("data-fw-form-amplify")) {
      onSuccess = function(data) {
        amplify.publish(form.getAttribute("data-fw-form-amplify"), data);
      };
    }

    if (!validate(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }

    Html.runClick();
    apiName = form.getAttribute("data-fw-dyn-action");

    if (apiName) {
      event.preventDefault();
      api = ApiServer[apiName];
      if (!api) {
        throw new Error("no Api named " + apiName);
      }
      await api(form, onSuccess);
      return false;
    } else if (onSuccess) {
      onSuccess();
      event.stopImmediatePropagation();
    }
  } finally {
    form.removeAttribute(FORM_VALIDATION_ATTRIBUTE);
    // remove lock
    form.removeAttribute('data-submit-running');
  }
}, true);

/**
 * Registers a submit handler with automatic validation and submit-running state management
 *
 * @param form - The form element to attach handler to
 * @param submitHandler - Async function to call on successful validation, receives (form, event)
 *
 * @example
 * addSubmitHandler(form, async (form, event) => {
 *   await ApiServer.taskcreate2(form);
 *   modal.closeModal();
 * });
 */
export function addSubmitHandler(
  form: HTMLFormElement,
  submitHandler: (form: HTMLFormElement, event: Event) => Promise<void>
): void {
  form.addEventListener('submit', async function(event) {
    event.preventDefault();

    // Prevent duplicate submissions
    if (form.getAttribute('data-submit-running')) {
      console.log('[FORMS] Form already running, preventing submit');
      return;
    }

    try {
      // Lock form
      form.setAttribute('data-submit-running', 'true');

      // Validate form
      if (!validate(form)) {
        return;
      }

      // Execute custom submit handler
      await submitHandler(form, event);

    } finally {
      // Always unlock form, even if error occurs
      form.removeAttribute(FORM_VALIDATION_ATTRIBUTE);
      form.removeAttribute('data-submit-running');
    }
  });
}

// Public API - Original jQuery-based functions
export {
  checkInput,
  validate,
  markValidNative,
  markInvalidNative
};

// Re-export types for external use
export type {
  ValidationFunction
};


